// Review Section Component
import {
    FiCheck,
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiWind,
} from 'react-icons/fi';
import {
    MdSolarPower,
    MdElectricalServices,
} from 'react-icons/md';

const ReviewSection = ({ serviceType, solarData, electricalData, generatorData, contactData }) => {
    // Helper function to get contact method label
    const getContactMethodLabel = (method) => {
        const methods = {
            phone: 'Phone Call',
            email: 'Email',
            whatsapp: 'WhatsApp',
            sms: 'SMS'
        };
        return methods[method] || 'Phone Call';
    };

    // Helper function to get best time label
    const getBestTimeLabel = (time) => {
        const times = {
            morning: 'Morning (8am-12pm)',
            afternoon: 'Afternoon (12pm-4pm)',
            evening: 'Evening (4pm-8pm)',
            anytime: 'Anytime'
        };
        return times[time] || 'Anytime';
    };

    // Helper function to get service labels
    const getServiceLabel = (serviceId) => {
        const services = {
            'rewiring': 'Complete Rewiring',
            'panel-upgrade': 'Panel Upgrade',
            'lighting': 'LED Lighting Installation',
            'outlets': 'New Outlets',
            'ev-charger': 'EV Charger Installation',
            'generator': 'Generator Installation',
            'surge': 'Surge Protection',
            'inspection': 'Safety Inspection'
        };
        return services[serviceId] || serviceId;
    };

    // Helper for generator service type labels
    const getGeneratorServiceLabel = (serviceId) => {
        const labels = {
            supply: 'Generator Supply & Installation',
            maintenance: 'Generator Maintenance & Servicing',
            hire: 'Generator Hire & Rental',
            transfer_switch: 'Automatic Transfer Switch Installation',
        };
        return labels[serviceId] || serviceId;
    };

    const getGeneratorKvaLabel = (kvaId) => {
        const labels = {
            small: 'Small (5–20 kVA)',
            medium: 'Medium (20–100 kVA)',
            large: 'Large (100–500 kVA)',
            industrial: 'Industrial (500+ kVA)',
        };
        return labels[kvaId] || kvaId;
    };

    const getGeneratorPurposeLabel = (purposeId) => {
        const labels = {
            residential: 'Residential Backup',
            commercial: 'Commercial Backup',
            industrial: 'Industrial Backup',
            events: 'Events & Temporary Power',
            construction: 'Construction Site',
        };
        return labels[purposeId] || purposeId;
    };

    const getHireDurationLabel = (durationId) => {
        const labels = {
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
            long_term: 'Long-term (3+ months)',
        };
        return labels[durationId] || durationId;
    };

    // Get solar calculated results if available
    const solarResults = solarData?.calculatedResults;

    return (
        <div className="bg-white rounded-xl shadow-lg p-8" data-aos="fade-up">
            <div className="flex items-center mb-8">
                <div className="p-3 bg-amber-100 rounded-lg mr-4">
                    <FiCheck className="text-3xl text-amber-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Review & Submit</h2>
                    <p className="text-gray-600">Review your information before submitting</p>
                </div>
            </div>

            <div className="space-y-6">
                {/* Solar System Summary */}
                {(serviceType === 'solar' || serviceType === 'all') && solarResults && (
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <MdSolarPower className="mr-3 text-green-600" />
                            Solar System Summary
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600">System Size</div>
                                <div className="text-lg font-bold text-green-600">{solarResults.systemSizeKW} kW</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600">Total Investment</div>
                                <div className="text-lg font-bold text-green-600">E{solarResults.totalCost.toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600">Monthly Savings</div>
                                <div className="text-lg font-bold text-green-600">E{solarResults.monthlySavings.toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">Monthly Bill</div>
                                <div className="text-sm font-semibold text-gray-800">E{solarData.monthlyBill.toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">System Type</div>
                                <div className="text-sm font-semibold text-gray-800 capitalize">{solarData.systemType}</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">Roof Type</div>
                                <div className="text-sm font-semibold text-gray-800 capitalize">{solarData.roofType}</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">Battery Backup</div>
                                <div className="text-sm font-semibold text-gray-800">{solarData.batteryBackup === 'yes' ? `Yes (${solarData.backupHours}h)` : 'No'}</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">Panels</div>
                                <div className="text-sm font-semibold text-gray-800">{solarResults.panelCount} × 400W</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                                <div className="text-xs text-gray-600">Payback Period</div>
                                <div className="text-sm font-semibold text-gray-800">{solarResults.paybackYears} years</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Electrical Services Summary */}
                {(serviceType === 'electrical' || serviceType === 'all') && electricalData && electricalData.selectedServices && electricalData.selectedServices.length > 0 && (
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <MdElectricalServices className="mr-3 text-blue-600" />
                            Electrical Services Summary
                        </h3>
                        <div className="space-y-3">
                            {electricalData.selectedServices.map((serviceId, index) => (
                                <div key={index} className="flex justify-between items-center bg-white p-4 rounded-lg">
                                    <div>
                                        <div className="font-medium text-gray-800">{getServiceLabel(serviceId)}</div>
                                        <div className="text-sm text-gray-600 capitalize">
                                            {electricalData.propertyType} • {electricalData.propertySize} • {electricalData.urgency}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {electricalData.additionalDetails && (
                            <div className="mt-4 bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Additional Details:</div>
                                <div className="text-sm text-gray-800">{electricalData.additionalDetails}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Generator Services Summary */}
                {(serviceType === 'generator' || serviceType === 'all') && generatorData && (
                    <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <FiWind className="mr-3 text-green-600" />
                            Generator Services Summary
                        </h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                                <div>
                                    <div className="font-medium text-gray-800">{getGeneratorServiceLabel(generatorData.generatorServiceType)}</div>
                                    <div className="text-sm text-gray-600">{getGeneratorKvaLabel(generatorData.generatorKvaRange)}</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white p-3 rounded-lg">
                                    <div className="text-xs text-gray-500">Purpose</div>
                                    <div className="text-sm font-semibold text-gray-800">{getGeneratorPurposeLabel(generatorData.generatorPurpose)}</div>
                                </div>
                                {generatorData.generatorFuelType && (
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="text-xs text-gray-500">Fuel Type</div>
                                        <div className="text-sm font-semibold text-gray-800 capitalize">{generatorData.generatorFuelType.replace('_', ' ')}</div>
                                    </div>
                                )}
                                {generatorData.generatorServiceType === 'hire' && generatorData.generatorHireDuration && (
                                    <div className="bg-white p-3 rounded-lg">
                                        <div className="text-xs text-gray-500">Hire Duration</div>
                                        <div className="text-sm font-semibold text-gray-800">{getHireDurationLabel(generatorData.generatorHireDuration)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {generatorData.generatorAdditionalDetails && (
                            <div className="mt-4 bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Additional Details:</div>
                                <div className="text-sm text-gray-800">{generatorData.generatorAdditionalDetails}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Contact Information */}
                {contactData && (
                    <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <FiUser className="mr-3 text-purple-600" />
                            Contact Information
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600 flex items-center mb-1">
                                    <FiUser className="mr-2" /> Name
                                </div>
                                <div className="font-medium text-gray-800">{contactData.name || 'Not provided'}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600 flex items-center mb-1">
                                    <FiMail className="mr-2" /> Email
                                </div>
                                <div className="font-medium text-gray-800">{contactData.email || 'Not provided'}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600 flex items-center mb-1">
                                    <FiPhone className="mr-2" /> Phone
                                </div>
                                <div className="font-medium text-gray-800">{contactData.phone || 'Not provided'}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600">Preferred Contact</div>
                                <div className="font-medium text-gray-800">{getContactMethodLabel(contactData.preferredContact)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg md:col-span-2">
                                <div className="text-sm text-gray-600 flex items-center mb-1">
                                    <FiMapPin className="mr-2" /> Address
                                </div>
                                <div className="font-medium text-gray-800">{contactData.address || 'Not provided'}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                                <div className="text-sm text-gray-600">Best Time to Contact</div>
                                <div className="font-medium text-gray-800">{getBestTimeLabel(contactData.bestTime)}</div>
                            </div>
                            {contactData.appointmentDate && (
                                <div className="bg-white p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Preferred Date</div>
                                    <div className="font-medium text-gray-800">{new Date(contactData.appointmentDate).toLocaleDateString()}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Next Steps */}
                <div className="bg-amber-50 p-6 rounded-lg border border-amber-200">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Next Steps</h3>
                    <ul className="space-y-3">
                        <li className="flex items-start">
                            <div className="bg-amber-100 p-2 rounded-lg mr-3">
                                <span className="font-bold text-amber-600">1</span>
                            </div>
                            <div>
                                <div className="font-medium text-gray-800">Free Consultation Call</div>
                                <div className="text-sm text-gray-600">Our expert will call within 24 hours</div>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <div className="bg-amber-100 p-2 rounded-lg mr-3">
                                <span className="font-bold text-amber-600">2</span>
                            </div>
                            <div>
                                <div className="font-medium text-gray-800">On-Site Assessment</div>
                                <div className="text-sm text-gray-600">Detailed evaluation of your property</div>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <div className="bg-amber-100 p-2 rounded-lg mr-3">
                                <span className="font-bold text-amber-600">3</span>
                            </div>
                            <div>
                                <div className="font-medium text-gray-800">Final Proposal</div>
                                <div className="text-sm text-gray-600">Customized quote with detailed breakdown</div>
                            </div>
                        </li>
                    </ul>
                </div>

                {/* Call to Action */}
                <div className="p-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold mb-2">Ready to Transform Your Energy?</h3>
                            <p className="opacity-90">Submit your quotation request and take the first step towards energy independence</p>
                        </div>
                        <FiCheck className="text-4xl opacity-80" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewSection;